/* eslint react/no-danger: 0 */
import React, { useContext, useEffect, useMemo } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Hidden from '@material-ui/core/Hidden';
import { makeStyles } from '@material-ui/core';

import { isEmpty } from 'lodash';
import config from '@/configs/config';
import menuContext from '../contexts/menu';

const useStyles = makeStyles((theme) => ({
  priceSummary: {
    display: 'flex',
    justifyContent: 'space-arround',
    padding: theme.spacing(1),

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'column',
      marginLeft: 'auto',
      padding: theme.spacing(1, 0),
      width: 200,
    },
  },
  priceContainer: {
    alignSelf: 'center',
    flexBasis: 0,
    flexGrow: 1,
    fontSize: 13,
    fontWeight: 'bold',
    textAlign: 'right',

    [theme.breakpoints.up('sm')]: {
      alignItems: 'center',
      display: 'flex',
      fontSize: 16,
      justifyContent: 'space-between',
      marginTop: theme.spacing(1),
      width: '100%',
    },
  },
}));

const ExpandableContent = styled.div`
  &:before{
    content: '感謝您的訂購，提醒您請務必注意以下取貨辦法 ▼';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    line-height: 40px;
    text-align: center;
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
  }
  position: relative;
  background-color: rgb(242, 242, 242);
  overflow: hidden;
  padding: 20px;
  height: 20px;
  &.open {
    &:before{
      content: '感謝您的訂購，提醒您請務必注意以下取貨辦法 ▲';
    }
    height: auto;
  }
`;

const Item = ({ data }) => {
  const {
    buyCount,
    fridgeAddress,
    fridgeName,
    orderId,
    price,
    productId,
    productName,
    productNumber,
    providerImage,
    redeemStartTime,
  } = data || {};

  const totalPrice = price * buyCount;
  const redeemTime = useMemo(() => {
    const statMoment = moment(redeemStartTime)
      .utcOffset(8)
      .set({ h: 12, m: 0 });
    return `${statMoment.format('MM/DD (dd) HH:mm')} ~ ${statMoment.clone().add(1, 'd').set({ h: 10, m: 0 }).format('MM/DD (dd) HH:mm')}`;
  }, [redeemStartTime]);

  return (
    <Box display="flex" my={1} key={productId}>
      <Hidden only="xs">
        <img
          style={{
            display: 'inline-block',
            height: '200px',
            marginRight: '10px',
            objectFit: 'cover',
            verticalAlign: 'top',
            width: '200px',
          }}
          src={`${config.RESOURCE_URI}/ProductImages/${productNumber}.jpg`}
          onError={(e) => {
            e.target.src = `${config.RESOURCE_URI}${providerImage}`;
          }}
          alt=""
        />
      </Hidden>
      <Box bgcolor="rgb(242, 242, 242)" p={3} flex="1" color="rgb(83,83,83)">
        <Box fontWeight="bold" color="black">訂單明細（已完成結帳）</Box>
        <Hidden only="sm"><hr /></Hidden>
        <Grid container spacing={1} style={{ marginTop: 8 }}>
          <Grid container item spacing={1} style={{ justifyContent: 'space-between' }}>
            <Grid item>商品名稱</Grid>
            <Grid item>{productName}</Grid>
          </Grid>
          <Grid container item spacing={1} style={{ justifyContent: 'space-between' }}>
            <Grid item>訂購數量：</Grid>
            <Grid item>{buyCount}</Grid>
          </Grid>
          <Grid container item spacing={1} style={{ justifyContent: 'space-between' }}>
            <Grid item>金額總計：</Grid>
            <Grid item>{(totalPrice) || 0}</Grid>
          </Grid>
          <Grid container item spacing={1} style={{ justifyContent: 'space-between' }}>
            <Grid item>訂單編號：</Grid>
            <Grid item>{orderId}</Grid>
          </Grid>
          <Grid container item spacing={1} style={{ justifyContent: 'space-between' }}>
            <Grid item>取貨地點：</Grid>
            <Grid item>
              <Box textAlign="right" fontWeight="bold">{`【${config.BrandNameTW}】${fridgeName}`}</Box>
              <Box textAlign="right" fontSize="14px">{fridgeAddress}</Box>
            </Grid>
          </Grid>
          <Grid container spacing={1} style={{ justifyContent: 'space-between' }}>
            <Grid item>取貨時間：</Grid>
            <Grid item style={{ textAlign: 'right' }}>
              <Box color="rgb(168,0,0)" fontWeight="bold" mt={1}>
                {redeemTime}
              </Box>
              <Box color="rgb(168,0,0)" fontWeight="bold" fontSize={10}>
                (實際取貨時間請以LINE 貨到通知為準)
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const Receipt = () => {
  const classes = useStyles();
  const history = useHistory();
  const { menuCache: { fridges: fridgeDictionary } = {} } = useContext(menuContext) || {};
  const {
    orderId, items, urlToGo, totalPrice, discount,
  } = history?.location?.state || {};

  useEffect(() => {
    if (isEmpty(items)) history.replace('/');
  }, [items, history]);

  const receiptHeader = (
    <>
      <Hidden only="xs">
        <Box position="relative" width={1} height={120} my={2}>
          <img
            src="/images/PreOrder/checkout_banner_desktop.jpg"
            style={{ objectFit: 'cover', height: '100%', width: '100%' }}
            alt=""
          />
        </Box>
        <Box fontWeight="fontWeightBold" fontSize="20px" pl="210px">感謝您已完成訂購</Box>
        <Box pl="210px">以下為您的訂購資訊</Box>
      </Hidden>
      <Hidden smUp>
        <Box position="relative">
          <Box bgcolor="rgb(242, 242, 242)">
            <img src="/images/PreOrder/background.png" style={{ width: '100%', maxHeight: 300 }} alt="" />
          </Box>
          <Box
            position="absolute"
            width={1}
            bottom={60}
          >
            <Grid container direction="column" justify="center" spacing={1}>
              <Grid item>
                <Box
                  color="primary.main"
                  fontSize="h5.fontSize"
                  fontWeight="fontWeightBold"
                  textAlign="center"
                >
                  感謝您已完成訂購
                </Box>
              </Grid>
              <Grid item>
                <Box
                  color="common.white"
                  fontSize="body1.fontSize"
                  fontWeight="fontWeightBold"
                  textAlign="center"
                >
                  以下為您的訂購資訊
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Hidden>
    </>
  );

  const receiptFooter = (
    <Hidden smUp>
      <Box
        width={1}
        position="fixed"
        left={0}
        bottom={0}
        bgcolor="common.white"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
      >
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => history.push(urlToGo || '/menu')}
        >
          <Box
            py={2}
            fontSize="body1.fontSize"
            fontWeight="fontWeightBold"
          >
            訂購完成
          </Box>
        </Button>
      </Box>
    </Hidden>
  );

  return (
    <div className={`
      ${classes.root}
      fz-container
      fz-m-auto
      fz-pb-10
      fz-text-left
      fz-bg-[#f2f2f2]
      md:fz-bg-white
      md:fz-pb-1
    `}
    >
      {receiptHeader}
      <Box
        py={2}
        mb={2}
      >
        { (items || []).map((data) => (
          <Item
            data={{
              ...data,
              fridgeName: (fridgeDictionary || {})[data?.fridgeId]?.name || '',
              fridgeAddress: (fridgeDictionary || {})[data?.fridgeId]?.address || '',
              orderId,
            }}
            key={data.productId}
          />
        ))}
        <div className={classes.priceSummary}>
          <div className={classes.priceContainer}>
            <div>
              商品總金額
            </div>
            <div>
              {`$${totalPrice}`}
            </div>
          </div>
          {discount
          && (
          <div className={classes.priceContainer}>
            <div>
              折扣總金額
            </div>
            <div>
              {`$${discount}`}
            </div>
          </div>
          )}
          <div className={classes.priceContainer}>
            <div>
              支付總金額
            </div>
            <div style={{ color: '#a80000' }}>
              {`$${totalPrice}`}
            </div>
          </div>
        </div>
        <ExpandableContent
          className={(items || []).length === 1 ? 'open' : ''}
          onClick={(event) => {
            if (event.target.className.indexOf('open') < 0) {
              event.target.className += ' open';
            } else {
              event.target.className = event.target.className.replace(' open', '');
            }
          }}
        >
          <Box mt={2}>【取貨流程】</Box>
          <div>
            1. 請務必先加入
            {config.BrandNameTW}
            LINE好友，以收到貨到通知訊息。
          </div>
          <div>2. 商品上架後，LINE將會收到專屬預訂取貨碼。</div>
          <div>3. 至指定機台使用信用卡或LINE Pay開門</div>
          <div>5. 在螢幕上確認手機號碼</div>
          <div>6. 點選「預購取貨」</div>
          <div>7. 輸入6位取貨碼後開門領取即可。</div>
          <img src={`${config.RESOURCE_URI}/PO/PO_guide.jpg`} alt="" style={{ marginTop: 8, width: '100%' }} />
          <Box mt={2}>【注意事項】</Box>
          <div>1. 請注意，預訂商品不得與優惠券或其他優惠活動共同使用。</div>
          <div>
            <span>2. 商品屬需保鮮之產品，為維護最佳賞味品質，請務必於收到取貨碼「當日」完成取貨！</span>
            <span>
              如於規定之取貨時段內未完成取貨，商品將下架，基於食品安全考量將無法再售出。因此恕不接受取消訂單與退款，訂購前請務必確認可配合本次商品取貨時段。
            </span>
          </div>
          <div>
            3. 如需取消訂單，最晚請於取貨當日9:30前通知客服人員處理。如於「取貨當日9:30（含）之後」取消，恕不接受取消訂單與退款，訂購前請務必考慮清楚，敬請見諒。
          </div>
          <div>4. 預訂商品如遇瑕疵或其他狀況，請向Line專人客服聯繫，將有專人協助進行處理。</div>
          <div>
            <span>
              5. 依消保法第十九條第二項及行政院公佈之通訊交易解除權合理例外情事適用準則，預訂商品屬「易於腐敗、保存期限較短或解約時即將逾期」之商品，
            </span>
            <span>
              因此除上述（4.）原因外，皆無提供7天鑑賞期退換貨之服務。
            </span>
          </div>
          <div>6.  部分取貨點設於私人產業內，故不開放員工以外之人員取貨，敬請見諒。</div>
          <div>7. 預訂商品圖片僅供參考，實際內容物依提供之商品規格資訊與實際供貨樣式為準。</div>
        </ExpandableContent>
      </Box>
      {receiptFooter}
    </div>
  );
};

export default Receipt;
